import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";
import { ShieldCheck, Eye, Compass, BookOpen } from "lucide-react";

const pillars = [
  {
    icon: ShieldCheck,
    title: "Ασφάλεια",
    desc: "Ένα μη επικριτικό περιβάλλον όπου μπορείς να εκφραστείς ελεύθερα και με εμπιστοσύνη.",
  },
  {
    icon: Eye,
    title: "Κατανόηση",
    desc: "Διερεύνηση των σκέψεων, συναισθημάτων και των μοτίβων που διαμορφώνουν τη ζωή σου.",
  },
  {
    icon: Compass,
    title: "Αλλαγή",
    desc: "Σταδιακή, ρεαλιστική και διαρκής αλλαγή μέσα από νέα γνωστικά και συμπεριφορικά εργαλεία.",
  },
  {
    icon: BookOpen,
    title: "Επιστημονική τεκμηρίωση",
    desc: "Όλες οι παρεμβάσεις βασίζονται σε σύγχρονα ερευνητικά δεδομένα ψυχολογίας.",
  },
];

export default function Approach() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="py-24 sm:py-36 relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-primary/[0.06]" />
      <div className="absolute -top-32 left-1/4 w-[40%] h-[60%] bg-primary/15 blur-[120px] rounded-full -z-10" />

      <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20 text-center">
        <motion.p
          {...fadeUp}
          className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-6"
        >
          Η Θεραπευτική Προσέγγιση
        </motion.p>
        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          data-testid="approach-heading"
          className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[68px] leading-[1.04]"
        >
          Γνωσιακή Συμπεριφορική
          <br />
          <span className="italic font-light">Ψυχοθεραπεία (CBT).</span>
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="mt-8 max-w-[720px] mx-auto font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed"
        >
          Η CBT είναι μια από τις πιο μελετημένες και αποτελεσματικές μορφές
          ψυχοθεραπείας. Εστιάζει στο παρόν και εξετάζει πώς οι σκέψεις
          επηρεάζουν τα συναισθήματα και τη συμπεριφορά μας — δίνοντάς σου
          συγκεκριμένα εργαλεία για να δημιουργήσεις πιο υγιή μοτίβα.
        </motion.p>
      </div>

      <motion.div
        {...stagger(0.1)}
        className="mt-16 sm:mt-20 max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {pillars.map((p) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              variants={{
                initial: { opacity: 0, y: 24 },
                whileInView: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                },
              }}
              data-testid={`pillar-${p.title}`}
              className="bg-background/70 backdrop-blur-sm border border-border/50 rounded-3xl p-7 text-left hover:bg-background transition-colors duration-500"
            >
              <div className="w-11 h-11 rounded-xl bg-card flex items-center justify-center text-primary mb-5">
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl text-foreground font-medium tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed text-base">
                {p.desc}
              </p>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}
